import React, {FC, useEffect, useRef} from 'react';

interface CounterProps {
  target: number;
}

/**
 * 数字计数器，移植自设计稿 js/main.js：
 * 进入视口 50% 后开始，2000ms 内按 16ms 步长 rAF 递增（step = target/125），到达即 unobserve。
 * 契约：初始文本是真实数字（JSX 渲染 {target}），动画首帧置 0 —— 两端都要保留。
 * startedRef 守卫 StrictMode 双挂载导致的重复启动。
 */
const Counter: FC<CounterProps> = ({target}) => {
  const ref = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    let rafId = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || startedRef.current) return;
          startedRef.current = true;
          observer.unobserve(el);

          const duration = 2000;
          const step = target / (duration / 16);
          let current = 0;
          el.textContent = '0'; // 动画首帧置 0

          const tick = () => {
            current += step;
            if (current < target) {
              el.textContent = String(Math.floor(current));
              rafId = window.requestAnimationFrame(tick);
            } else {
              el.textContent = String(target);
            }
          };
          rafId = window.requestAnimationFrame(tick);
        });
      },
      {threshold: 0.5},
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [target]);

  return (
    <div
      className="font-display font-bold text-4xl lg:text-5xl text-slate-900 counter-value"
      data-target={target}
      ref={ref}
    >
      {target}
    </div>
  );
};

export default Counter;
